import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ReceiptHeader } from "@/components/ReceiptDialog";
import { ChangePasswordCard } from "@/components/ChangePasswordCard";
import { MenuLinkCard } from "@/components/MenuLinkCard";
import { DeliverySettingsFields } from "@/components/DeliverySettingsFields";
import { useStore } from "@/context/StoreContext";
import { api, errMsg } from "@/lib/api";

export default function Pengaturan() {
  const { store, setStore } = useStore();
  const [form, setForm] = useState(store);
  const [saving, setSaving] = useState(false);

  useEffect(() => setForm(store), [store]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const public_url = (form.public_url || "").trim().replace(/^(https?:\/\/[^/]+).*$/, "$1");
      const zones = (form.zones || []).map((z) => ({ name: z.name.trim(), fee: Number(z.fee) || 0 }));
      if (form.delivery_mode === "zona" && !zones.length) throw new Error("Tambahkan minimal 1 zona pengiriman");
      const { data } = await api.put("/settings", { ...form, name: form.name.trim(), whatsapp: form.whatsapp.trim(), public_url,
        rate_per_km: Number(form.rate_per_km) || 0, zones });
      setStore(data);
      toast.success("Pengaturan toko disimpan");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="pengaturan-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl">Pengaturan Toko</h1>
        <p className="mt-1 text-sm text-slate-500">Informasi ini tampil di bagian atas struk.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <form onSubmit={submit} className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6" data-testid="settings-form">
          <div className="space-y-1.5">
            <Label htmlFor="store-name">Nama Toko</Label>
            <Input id="store-name" required maxLength={80} value={form.name} onChange={set("name")} data-testid="settings-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="store-address">Alamat</Label>
            <Textarea id="store-address" rows={3} maxLength={200} value={form.address} onChange={set("address")} placeholder="Jl. ..." data-testid="settings-address-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="store-wa">Nomor WhatsApp</Label>
            <Input id="store-wa" inputMode="tel" maxLength={20} pattern="[0-9+\- ]*" value={form.whatsapp} onChange={set("whatsapp")} placeholder="08xxxxxxxxxx" data-testid="settings-whatsapp-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="store-public-url">Alamat Aplikasi (yang sudah dipublikasikan)</Label>
            <Input id="store-public-url" type="url" maxLength={200} value={form.public_url || ""} onChange={set("public_url")} placeholder="https://nama-aplikasi-anda.emergent.host" data-testid="settings-public-url-input" />
            <p className="text-xs text-slate-500">Dipakai untuk link menu pelanggan dan link pesanan di WhatsApp.</p>
          </div>
          <DeliverySettingsFields form={form} setForm={setForm} />
          <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="settings-save-button">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Simpan
          </Button>
        </form>

        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-5">
          <p className="mb-3 text-xs font-medium uppercase tracking-wider text-slate-500">Pratinjau Struk</p>
          <div className="mx-auto max-w-[320px] font-mono text-xs text-black" data-testid="settings-receipt-preview">
            <ReceiptHeader store={form} />
          </div>
        </div>
      </div>

      <div className="max-w-xl space-y-6">
        <MenuLinkCard hasWa={!!store.whatsapp} publicUrl={store.public_url} />
        <ChangePasswordCard />
      </div>
    </div>
  );
}
